import {CookieSerializeOptions} from 'cookie';

export enum KakaoAuthTokenType {
  AccessToken = 'accessToken',
  AccessTokenExpireDate = 'accessTokenExpireDate',
  RefreshToken = 'refreshToken',
  AppUserId = 'appUserId',
  RefreshTokenExpireDate = 'refreshTokenExpireDate',
}

export enum UserInfoTokenType {
  Age = 'age',
  AuthenticatedAt = 'authenticatedAt',
  MarketingAgreement = 'marketingAgreement',
}

export const COOKIE_KEY_WEB_JWT = '_kpwtkn';

export const JWT_ALGORITHM = 'ES256';
export const JWT_ISSUER = 'urn:page.kakao.com:issuer';
export const JWT_AUDIENCE = 'urn:page.kakao.com:audience';

// 기본 24시간이지만 만약 expireTime 옵션이 들어오면 해당 옵션을 사용합니다
export const JWT_TOKEN_EXPIRATION_TIME = '24h';
export const JWT_TOKENEXPIRE_SECONDS = 24 * 60 * 60;
export const JWT_TOKEN_EXPIRE_MILLISECONDS = JWT_TOKENEXPIRE_SECONDS * 1000;

// 카카오 엑세스토큰 기본 만료 시간은 2시간 입니다
// https://developers.kakao.com/docs/latest/ko/kakaologin/common#token
export const DEFAULT_KAKAO_ACCESS_TOKEN_EXPIRE_SECONDS = 2 * 60 * 60;
export const DEFAULT_KAKAO_ACCESS_TOKEN_EXPIRE_MILLSECONDS =
  DEFAULT_KAKAO_ACCESS_TOKEN_EXPIRE_SECONDS * 1000;

/**
 * sameSite: Lax 인 경우 다른 사이트(tv 포인트)에 이동했다가 돌아올 때 쿠키 전달이 안되는 현상이 있어 sameSite: none 쿠키를 추가합니다. 이 쿠키는 암호화 되어 있습니다. (암호키는 빌링에 전달)
 */
export const COOKIE_KEY_BILLING_ACCESS_TOKEN_ENCRYPTED = '_kpawbat_e';
export const WEBVIEW_LOGIN_COOKIE_EXPIRATION_TIME = 60 * 60 * 24; // 24시간
export const WEBVIEW_LOGIN_COOKIE_EXPIRATION_MILLISECONDS =
  WEBVIEW_LOGIN_COOKIE_EXPIRATION_TIME * 1000;
export const BILLING_ACCESS_TOKEN_COOKIE_EXPIRATION_TIME = 60 * 60 * 2; // 2시간(API 기본)
export const CERT_PARAMS_COOKIE_EXPIRATION_TIME = 60 * 5;

/**
 * 원래 오픈 전 next 여부를 nginx 에서 분기하기 위한 쿠키지만 로깅에도 사용함. 이 쿠키가 있으면 로깅을 진행함
 */
export const COOKIE_KEY_TOGGLE_NEXT = '_kpwnext';

/**
 * 카카오 로그인 창에서 "간편로그인 정보 저장"을 체크하지 않는 경우 _kawlt 쿠키가 세션 쿠키로 남습니다
 *  - 이러면 브라우저를 완전히 종료하면 로그인이 풀리게 됩니다.
 * 로그인 창에서 "간편로그인 정보 저장"을 체크한 경우_kawlt 쿠키가 한달 기간의 쿠키로 남습니다.
 *  - 브라우저 완전히 종료해도 한달간 로그인 유지됩니다
 * 웹에서 인증 상태임에도 _kawlt 가 없는 상황은 존재하면 안됩니다.
 */
export const COOKIE_KEY_KAKAO_ACCOUNT_WEB_LOGIN_TOKEN = '_kawlt';
/**
 * 회원가입 시 생성되는 쿠키. 1회성 로그인 쿠키로 보인다. 이 값이 있으면 카카오SDK 에서 자동 로그인이 된다
 * https://podo.agit.in/g/368737/wall/302754257#comment_panel_302754542
 */
export const COOKIE_KEY_KAKAO_ACCOUNT_APP_SIGNUP_TOKEN = '_maldive_oauth_si';

/**
 * NICE 본인인증 callback_url 250 bytes 제한으로,
 * process_id 외 나머지 인증 파라미터를 cookie로 전달할 때 사용합니다.
 */
export const COOKIE_KEY_CERT_PARAMS = '_kpcert';

export const KAKAO_COOKIES = [
  COOKIE_KEY_KAKAO_ACCOUNT_WEB_LOGIN_TOKEN,
  COOKIE_KEY_KAKAO_ACCOUNT_APP_SIGNUP_TOKEN,
];

export const BILLING_COOKIES = [COOKIE_KEY_BILLING_ACCESS_TOKEN_ENCRYPTED];

export const DEFAULT_COOKIE_SERIALIZE_OPTIONS: CookieSerializeOptions = {
  path: '/',
  domain: '.kakao.com',
  httpOnly: true,
  secure: true,
  sameSite:
    'lax' /** 외부 도메인에서 GET 통해 들어오게 될 때도 유지되어야 하기 때문에 lax 로 유지합니다 */,
};

export const SHARED_COOKIE_SERIALIZE_OPTIONS: CookieSerializeOptions = {
  path: '/',
  domain: '.kakao.com',
  httpOnly: true,
  secure: true,
  sameSite:
    'none' /** 외부 사이트로 이동한 뒤 돌아오는 경우에도 유지되어야하는 쿠키(예: 빌링 -> TV 포인트 -> 빌링), 드림시큐리티 POST 요청*/,
};

export const JWT_COOKIE_SERIALIZE_OPTIONS: CookieSerializeOptions = {
  ...DEFAULT_COOKIE_SERIALIZE_OPTIONS,
};

export const WEBVIEW_JWT_COOKIE_SERIALIZE_OPTIONS: CookieSerializeOptions = {
  ...DEFAULT_COOKIE_SERIALIZE_OPTIONS,
  maxAge: WEBVIEW_LOGIN_COOKIE_EXPIRATION_TIME,
};

export const BILLING_ACCESS_TOKEN_COOKIE_SERIALIZE_OPTIONS: CookieSerializeOptions =
  {
    ...DEFAULT_COOKIE_SERIALIZE_OPTIONS,
    maxAge: BILLING_ACCESS_TOKEN_COOKIE_EXPIRATION_TIME,
  };

export const BILLING_ACCESS_TOKEN_COOKIE_ENCRYPTED_SERIALIZE_OPTIONS: CookieSerializeOptions =
  {
    ...SHARED_COOKIE_SERIALIZE_OPTIONS,
    maxAge: BILLING_ACCESS_TOKEN_COOKIE_EXPIRATION_TIME,
  };

export const CERT_PARAMS_COOKIE_SERIALIZE_OPTIONS: CookieSerializeOptions = {
  ...SHARED_COOKIE_SERIALIZE_OPTIONS,
  maxAge: CERT_PARAMS_COOKIE_EXPIRATION_TIME,
  httpOnly: false,
};

/**
 * 게이트웨이와 통신에서는 반드시 이 토큰이 일치해야합니다.(Server-to-Server 검증)
 * 참고 : https://podotree.atlassian.net/wiki/spaces/PSD/pages/2606202920/API+Gateway#%EC%96%B4%EB%B7%B0%EC%A7%95-%EA%B0%90%EC%A7%80
 */
export const HEADER_GW_WEB_KEY = 'X-Service-Token';
export const HEADER_GW_WEB_TYPE = 'X-Web-Type';
export const HEADER_GW_WEB_TYPE_VALUE_MOBILE = 'MOBILE';
export const HEADER_GW_WEB_TYPE_VALUE_PC = 'PC';
export const HEADER_GW_WEB_VALUE = 'pageweb';
export const HEADER_DEVICE_ID = 'X-Device-Id';
export const AUTH_HEADER_STOKEN = 'X-Stoken';
export const AUTH_HEADER_USER_ID = 'X-User-Id';
export const HEADER_DEVICE_LANG = 'X-Device-Lang';
export const HEADER_DEVICE_MODEL = 'X-Device-Model';
export const HEADER_SERVICE_LANG = 'X-Service-Lang';
export const HEADER_X_STORE_TYPE = 'x-store-type';
export const AUTH_HEADER_ACCESS_TOKEN = 'X-Access-Token';
export const HEADER_X_FORWARDED_FOR = 'X-Forwarded-For';
/**
 * ingress 에서 실제 클라이언트 IP 가 X-Original-Forwarded-For 를 통해 넘어오고 있어 이 헤더를 우선합니다. X-Forwarded-For 에는 프록시한 서버 IP 만 담겨있습니다(compute 가 잘 되면 다시 바꿔도 무방)
 */
export const HEADER_X_ORIGINAL_FORWARDED_FOR = 'X-Original-Forwarded-For';
export const HEADER_X_REQUEST_ID = 'X-Request-ID';
/**
 * 사내업무용 API 사용을 위한 토큰
 * https://podotree.atlassian.net/wiki/spaces/kakaopagedev/pages/2913206277/NEXT
 */
export const HEADER_GW_MONITORING_KEY = 'X-Profile';
export const HEADER_GW_MONITORING_VALUE = 'monitoring';

/**
 * JWT 암호화 키. 아무 키나 상관 없어 아무 값이나 넣음
 */
export const JWT_ENCRYPT_KEY = 'E!@NC$RY0#F93#$@fdA##';

export const BILLING_ENCRYPT_IV = '0942342312432122';
export const BILLING_ENCRYPT_KEY = '0104030billingaccesstoken0132000';

/**
 * 웹뷰일때 가입/로그인 시 geoIp 를 전달주고 있는 헤더
 * 사용자 접속 환경의 거주국가(app/init 의 geoIp) 를 전달
 */
export const HEADER_X_GEO_IP = 'X-Geo-Ip';

/**
 * 클라에서 넘겨주는 country code
 * 사용자의 거주국가 코드. 가입 시 받는 Geo-Ip 값으로, 한번 저장되면 변경되지 않음
 */
export const HEADER_X_COUNTRY_CODE = 'X-Country-Code';
